import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Forces every route change (and the initial mount) to start at the top of the page,
 * and disables the browser's automatic scroll restoration so a hard refresh
 * also lands at the top instead of restoring the previous scroll position.
 */
const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Tell the browser to never restore scroll on history navigation/refresh.
    if ('scrollRestoration' in window.history) {
      try { window.history.scrollRestoration = 'manual'; } catch (_) { /* noop */ }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // If the URL points to an in-page anchor, scroll the target into view.
    if (hash && hash.length > 1) {
      const id = decodeURIComponent(hash.slice(1));
      // Wait a tick so the target route has mounted before querying.
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };
      if (!tryScroll()) {
        const r = requestAnimationFrame(() => { if (!tryScroll()) setTimeout(tryScroll, 80); });
        return () => cancelAnimationFrame(r);
      }
      return;
    }

    const toTop = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
      if (document?.documentElement) document.documentElement.scrollTop = 0;
      if (document?.body) document.body.scrollTop = 0;
    };

    // Fire immediately, then again after layout settles (covers cases where
    // images/fonts finish loading and shift content after the route change).
    toTop();
    const r1 = requestAnimationFrame(toTop);
    const r2 = requestAnimationFrame(() => requestAnimationFrame(toTop));
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
    };
  }, [pathname, search, hash]);

  return null;
};

export default ScrollToTop;
